import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import {
  REVIEW_SORTS,
  REVIEW_SORT_LABEL,
  listPartnerReviews,
  replyToReview,
  sortReviews,
  summarize,
  type ReviewSort,
} from "@/lib/reviews";

export const Route = createFileRoute("/_authenticated/partner/reviews")({
  head: () => ({
    meta: [
      { title: "Reviews & Ratings · Landoo Partner" },
      {
        name: "description",
        content: "See customer ratings and reviews for your Landoo listings and reply to them.",
      },
      { property: "og:title", content: "Reviews & Ratings · Landoo Partner" },
      {
        property: "og:description",
        content: "See customer ratings and reviews for your Landoo listings and reply to them.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartnerReviewsPage,
});

function PartnerReviewsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sort, setSort] = useState<ReviewSort>("latest");
  const [openId, setOpenId] = useState<string | null>(null);
  const [text, setText] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["partner", "reviews", user?.id],
    queryFn: () => listPartnerReviews(user!.id),
    enabled: !!user,
  });

  const summary = useMemo(() => summarize(rows.filter((r) => !r.is_hidden)), [rows]);
  const list = useMemo(() => sortReviews(rows, sort), [rows, sort]);

  const reply = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => replyToReview(id, value),
    onSuccess: () => {
      toast.success("Reply posted.");
      setOpenId(null);
      setText("");
      qc.invalidateQueries({ queryKey: ["partner", "reviews", user?.id] });
    },
    onError: () => toast.error("Could not post the reply."),
  });

  return (
    <DashboardShell title="Reviews & Ratings" role="Partner" items={PARTNER_NAV}>
      <div className="grid gap-3 sm:grid-cols-3">
        <Card label="Average Rating" value={summary.average ? `${summary.average} / 5` : "—"} />
        <Card label="Total Reviews" value={String(summary.total)} />
        <Card label="Replied" value={String(rows.filter((r) => r.partner_reply).length)} />
      </div>

      <div className="mt-4 flex justify-end">
        <Select value={sort} onValueChange={(v) => setSort(v as ReviewSort)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Sort reviews">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_SORTS.map((s) => (
              <SelectItem key={s} value={s}>
                {REVIEW_SORT_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading reviews…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No reviews yet. Customers can review a listing after they enquire about it.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((r) => (
            <li key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <StarRating value={r.rating} size="sm" />
                  <p className="mt-1 text-sm font-semibold text-brown-dark">
                    {r.listing_title ?? "Listing"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.customer_name ?? "Customer"} ·{" "}
                    {new Date(r.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {r.is_hidden ? " · Hidden by admin" : ""}
                  </p>
                </div>
                <span className="rounded-full bg-secondary px-2.5 py-1 text-xs capitalize text-brown-dark">
                  {r.service_type}
                </span>
              </div>
              {r.title && <p className="mt-2 text-sm font-medium text-brown-dark">{r.title}</p>}
              {r.body && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{r.body}</p>
              )}
              {r.partner_reply && (
                <div className="mt-3 rounded-lg bg-secondary/50 p-2.5">
                  <p className="text-xs font-semibold text-brown-dark">Your reply</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                    {r.partner_reply}
                  </p>
                </div>
              )}
              <div className="mt-3">
                {openId === r.id ? (
                  <div className="space-y-2">
                    <Textarea
                      rows={2}
                      maxLength={600}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Write your reply"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!text.trim() || reply.isPending}
                        onClick={() => reply.mutate({ id: r.id, value: text })}
                      >
                        Post reply
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setOpenId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setOpenId(r.id);
                      setText(r.partner_reply ?? "");
                    }}
                  >
                    {r.partner_reply ? "Edit reply" : "Reply to review"}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Star className="h-3.5 w-3.5 text-coral" aria-hidden /> {label}
      </div>
      <p className="mt-1 text-2xl font-bold text-brown-dark">{value}</p>
    </div>
  );
}
